import React, { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Snackbar,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { ArrowDropDown, ArrowDropUp, CloseOutlined, HistoryOutlined } from '@mui/icons-material';
import { Alert } from '@mui/lab';
import { COLORS } from '../../../utils/Application_Constants';
import { getExpenseHistory } from '../../Services/ExpenseService';
import { formatErrorMessage } from '../../Utils/ErrorMessage';

const styles = (theme) => ({
    DrawerPaper: {
        padding: '16px 40px 40px 40px',
        maxWidth: '400px',
        [theme.breakpoints.up('md')]: {
            width: '400px',
        },
    },
    historyHeader: {
        color: COLORS.LT_MIDNIGHT50,
        padding: '4px !important',
    },
    historyEvents: {
        paddingBottom: '64px',
    },
    historyEventDetail: {
        paddingLeft: '16px',
    },
    detailLabel: {
        fontWeight: '600',
        display: 'flex',
        cursor: 'pointer',
    },
    progressRoot: {
        margin: '40px auto',
    },
});

const ExpenseHistory = (props) => {
    const { classes, mode, expenseId } = props;
    const [open, setOpen] = useState(false);
    const [eventDetailOpen, setEventDetailOpen] = useState();
    const [expenseHistory, setExpenseHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [alert, setAlert] = useState();
    const isPrecoded = mode === 'pending-cc';

    async function fetchExpenseHistory() {
        setIsLoading(true);
        try {
            const history = await getExpenseHistory(expenseId, isPrecoded);
            setExpenseHistory(history);
        } catch (error) {
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    }

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert(null);
    };

    const handleDrawerOpen = () => {
        setOpen(true);
        fetchExpenseHistory();
    };

    const handleDrawerClose = () => {
        setOpen(false);
        setEventDetailOpen(null);
        setExpenseHistory([]);
    };

    const handleEventDetail = (index) => {
        const eventDetails = index === eventDetailOpen ? null : index;
        setEventDetailOpen(eventDetails);
    };

    const renderAlert = () => (
        <Snackbar
            open={Boolean(alert)}
            autoHideDuration={6000}
            onClose={handleAlertClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert onClose={handleAlertClose} severity={alert?.type}>
                {alert?.message}
            </Alert>
        </Snackbar>
    );

    const renderExpenseHistoryDrawer = () => (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleDrawerClose}
            classes={{ paper: classes.DrawerPaper }}
            sx={(theme) => ({
                zIndex: 30000,
            })}
        >
            <Grid container direction="column" spacing={2} className={classes.historyEvents}>
                <Grid item className={classes.historyHeader}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Grid container spacing={1} alignItems="center">
                                <HistoryOutlined fontSize="small" />
                                <Grid item>
                                    <Typography variant="overline" display="block">
                                        <b>Expense History</b>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={handleDrawerClose} size="large">
                                <CloseOutlined />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
                <Divider />
                {renderExpenseHistoryEvents()}
            </Grid>
        </Drawer>
    );

    const renderExpenseHistoryEvent = (event, index) => (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <Typography variant="body2">
                    <b>{event.headingOne}</b>
                </Typography>
            </Grid>
            <Grid item>
                <Typography variant="body2">{event.headingTwo}</Typography>
            </Grid>
            {event.details && event.details.length > 0 && (
                <Grid item>
                    <Typography
                        onClick={() => handleEventDetail(index)}
                        variant="caption"
                        className={classes.detailLabel}
                        gutterBottom
                    >
                        Details
                        {index !== eventDetailOpen ? (
                            <ArrowDropDown fontSize="small" />
                        ) : (
                            <ArrowDropUp fontSize="small" />
                        )}
                    </Typography>
                    <Collapse in={index === eventDetailOpen} timeout="auto" unmountOnExit>
                        <Grid container spacing={2}>
                            {event.details.map((detail, index) => (
                                <Grid item xs={12} key={index}>
                                    <Typography
                                        variant="body2"
                                        className={classes.historyEventDetail}
                                    >
                                        {detail.detail}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Collapse>
                </Grid>
            )}
        </Grid>
    );

    const renderExpenseHistoryEvents = () => (
        <>
            {isLoading && (
                <CircularProgress classes={{ root: classes.progressRoot }} color="primary" />
            )}
            {!isLoading && expenseHistory.length > 0 && (
                <>
                    {expenseHistory.map((event, index) => (
                        <React.Fragment key={index}>
                            <Grid item className={classes.historyEvent}>
                                {renderExpenseHistoryEvent(event, index)}
                            </Grid>
                            {index !== expenseHistory.length - 1 && (
                                <Grid item>
                                    <Divider />
                                </Grid>
                            )}
                        </React.Fragment>
                    ))}
                </>
            )}
            {!isLoading && expenseHistory.length === 0 && (
                <Grid item>
                    <Typography variant="body2">No history available.</Typography>
                </Grid>
            )}
        </>
    );

    return (
        <>
            <Button onClick={handleDrawerOpen} startIcon={<HistoryOutlined />} color="default">
                Expense History
            </Button>
            {renderExpenseHistoryDrawer()}
            {renderAlert()}
        </>
    );
};

export default withStyles(styles)(ExpenseHistory);
